import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { ArrowLeftIcon, IconSize, IconWrapper, StrongText, StyledFlex, StyledMargin } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { useAiAssistantRecordingGlobalState } from '../shared/useAiAssistantRecordingGlobalState';
import { ensureMicrophoneAccess } from '../audio/ensureMicrophoneAccess';
import { useAiAssistantTokensQuotaGuard } from '../shared/useAiAssistantTokensQuotaGuard';
import { SpinnerGradientDefs } from '../icons/SpinnerGradientDefs';

import { AiAssistantMainMenuTileItemContainer } from './AiAssistantMainMenuTileItemContainer';
import {
   StyledCollapsedSpinnerIcon,
   StyledLink,
   StyledStandardSpinner,
} from './AiAssistantMainMenuTileItemPending.styled';

const t = translationKeys.mainMenuTileItem;

type AiAssistantMainMenuTileItemPendingProps = { onStartTranscription: () => Promise<boolean> };

export const AiAssistantMainMenuTileItemPending = (props: AiAssistantMainMenuTileItemPendingProps): JSX.Element => {
   const theme = useTheme();
   const { resumeRecording } = useAiAssistantRecordingGlobalState();
   const verifyTokensQuota = useAiAssistantTokensQuotaGuard();

   const handleResumeRecording = useCallback(async () => {
      if (!(await verifyTokensQuota()) || !(await ensureMicrophoneAccess())) {
         return;
      }

      if (await props.onStartTranscription()) {
         resumeRecording();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onStartTranscription, resumeRecording, verifyTokensQuota]);

   return (
      <AiAssistantMainMenuTileItemContainer
         icon={
            <StyledCollapsedSpinnerIcon alignItems={'center'} justifyContent={'center'}>
               <>
                  <SpinnerGradientDefs />
                  <StyledStandardSpinner />
               </>
            </StyledCollapsedSpinnerIcon>
         }
         backgroundColor={theme.backgroundBody}
      >
         <StyledFlex direction={'column'} gap={8} alignItems={'center'} justifyContent={'space-between'}>
            <StyledFlex direction={'column'} gap={4} alignItems={'center'}>
               <>
                  <SpinnerGradientDefs />
                  <StyledStandardSpinner />
               </>
               <StrongText testId={'creatingNote'}>
                  <Translate>{t.creatingNote}</Translate>
               </StrongText>
            </StyledFlex>
            <StyledMargin top={6} bottom={6}>
               <StyledFlex alignItems={'center'}>
                  <IconWrapper testId={'left'} size={IconSize.SMALLEST}>
                     <ArrowLeftIcon />
                  </IconWrapper>
                  <StyledLink
                     label={translationKeys.mainMenuTileItem.resumeRecording}
                     onClick={handleResumeRecording}
                     testId={'resumeRecording'}
                  />
               </StyledFlex>
            </StyledMargin>
         </StyledFlex>
      </AiAssistantMainMenuTileItemContainer>
   );
};
